import React, { useState, useCallback, useRef, useMemo } from 'react';
import { GoogleMap, Polygon, InfoWindow, Marker } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';

const containerStyle = {
  width: '100%',
  height: '600px', // Made taller
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
};

const mapOptions = {
  mapTypeId: 'satellite',
  mapTypeControl: true,
  zoomControl: true,
  streetViewControl: true,
  fullscreenControl: true,
  styles: [
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [{ color: '#004358' }]
    },
    {
      featureType: 'landscape',
      elementType: 'geometry',
      stylers: [{ color: '#1f8a70' }]
    },
  ]
};

const infoWindowStyles = {
  container: {
    padding: '16px',
    maxWidth: '300px',
    fontFamily: 'Arial, sans-serif'
  },
  title: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '12px',
    color: '#2D3748',
    borderBottom: '2px solid #04BD9C',
    paddingBottom: '8px'
  },
  section: {
    marginBottom: '12px',
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    fontWeight: '600',
    color: '#4A5568',
    marginBottom: '4px'
  },
  value: {
    color: '#2D3748'
  },
  progressContainer: {
    marginTop: '8px',
    background: '#E2E8F0',
    borderRadius: '4px',
    height: '8px',
    overflow: 'hidden'
  },
  progressBar: (percentage) => ({
    width: `${percentage}%`,
    height: '100%',
    backgroundColor: '#04BD9C',
    transition: 'width 0.3s ease'
  }),
  progressText: {
    fontSize: '12px',
    color: '#718096',
    marginTop: '4px',
    textAlign: 'right'
  },
  description: {
    marginTop: '12px',
    padding: '8px',
    background: '#F7FAFC',
    borderRadius: '4px',
    fontSize: '14px',
    lineHeight: '1.4'
  }
};

// Helper function to validate coordinates
const isValidCoordinate = (coord) => {
  const lat = parseFloat(coord[1]);
  const lng = parseFloat(coord[0]);
  return !isNaN(lat) && !isNaN(lng) && 
         lat >= -90 && lat <= 90 && 
         lng >= -180 && lng <= 180;
};

const LocationMap = ({ data }) => {
  const { t } = useTranslation();
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [hoveredRegion, setHoveredRegion] = useState(null);
  const mapRef = useRef(null);
  const boundsSet = useRef(false);
  
  const getPolygonOptions = useCallback((region) => ({
    fillColor: '#04BD9C',
    fillOpacity: selectedRegion?.id === region.id ? 0.6 : 
                hoveredRegion?.id === region.id ? 0.4 : 0.3,
    strokeColor: selectedRegion?.id === region.id ? '#038C73' : '#04BD9C',
    strokeOpacity: 1,
    strokeWeight: selectedRegion?.id === region.id ? 3 : 2,
    clickable: true,
    zIndex: selectedRegion?.id === region.id ? 2 : 1
  }), [selectedRegion, hoveredRegion]);

  const allRegions = useMemo(() => {
    if (!data || !data[0] || !data[0].regions) {
      return [];
    }

    return data[0].regions
      .map(region => {
        try {
          if (!region.geofence?.coordinates?.[0]?.[0]) {
            return null;
          }

          // Validate coordinates before processing
          const validCoordinates = region.geofence.coordinates[0][0].filter(isValidCoordinate);
          
          if (validCoordinates.length === 0) {
            console.warn(`Invalid coordinates for region ${region.name}`);
            return null;
          }

          return {
            ...region,
            paths: validCoordinates.map(coord => ({
              lat: parseFloat(coord[1]),
              lng: parseFloat(coord[0])
            }))
          };
        } catch (error) {
          console.warn(`Error processing region ${region.name}:`, error);
          return null;
        }
      })
      .filter(Boolean);
  }, [data]);

  const bounds = useMemo(() => {
    if (allRegions.length === 0) {
      return null;
    }
    try {
      const bounds = new window.google.maps.LatLngBounds();
      allRegions.forEach(region => {
        if (region.paths && region.paths.length > 0) {
          region.paths.forEach(coord => {
            if (coord && typeof coord.lat === 'number' && typeof coord.lng === 'number') {
              bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
            }
          });
        }
      });
      return bounds;
    } catch (error) {
      console.warn('Error calculating bounds:', error);
      return null;
    }
  }, [allRegions]);

  const onMapLoad = useCallback((map) => {
    if (!map) return;
    mapRef.current = map;
    if (!boundsSet.current && bounds) {
      try {
        map.fitBounds(bounds);
        setTimeout(() => {
          boundsSet.current = true;
        }, 1000);
      } catch (error) {
        console.warn('Error fitting bounds:', error);
      }
    }
  }, [bounds]);

  const getPolygonCenter = useCallback((paths) => {
    try {
      if (!paths || paths.length === 0) {
        return null;
      }
      const bounds = new window.google.maps.LatLngBounds();
      paths.forEach(coord => {
        if (coord && typeof coord.lat === 'number' && typeof coord.lng === 'number') {
          bounds.extend(new window.google.maps.LatLng(coord.lat, coord.lng));
        }
      });
      return {
        lat: (bounds.getNorthEast().lat() + bounds.getSouthWest().lat()) / 2,
        lng: (bounds.getNorthEast().lng() + bounds.getSouthWest().lng()) / 2
      };
    } catch (error) {
      console.warn('Error calculating polygon center:', error);
      return null;
    }
  }, []);

  if (allRegions.length === 0) {
    return null;
  }

  return (
    <div style={{ position: 'relative' }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        options={mapOptions}
        onLoad={onMapLoad}
      >
        {allRegions.map((region) => {
          const center = getPolygonCenter(region.paths);
          if (!center) return null;

          return (
            <React.Fragment key={region.id}>
              <Polygon
                paths={region.paths}
                options={getPolygonOptions(region)}
                onClick={() => setSelectedRegion(region)}
                onMouseOver={() => setHoveredRegion(region)}
                onMouseOut={() => setHoveredRegion(null)}
              />
              {(!selectedRegion || selectedRegion.id !== region.id) && (
                <Marker
                  position={center}
                  label={{
                    text: region.name || '',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                  onClick={() => setSelectedRegion(region)}
                  options={{ clickable: true }}
                />
              )}
              {selectedRegion && selectedRegion.id === region.id && center && (
                <InfoWindow
                  position={center}
                  onCloseClick={() => setSelectedRegion(null)}
                >
                  <div style={infoWindowStyles.container}>
                    <div style={infoWindowStyles.title}>{selectedRegion.name}</div>
                    <div style={infoWindowStyles.section}>
                      <span style={infoWindowStyles.label}>{t('map.organization')}</span>
                      <span style={infoWindowStyles.value}>{selectedRegion.organization?.name || '-'}</span>
                    </div>
                    <div style={infoWindowStyles.section}>
                      <span style={infoWindowStyles.label}>{t('map.location')}</span>
                      <span style={infoWindowStyles.value}>
                        {t('map.coordinates', {
                          lat: Number(selectedRegion.center_lat).toFixed(6),
                          lng: Number(selectedRegion.center_lng).toFixed(6)
                        })}
                      </span>
                    </div>
                  </div>
                </InfoWindow>
              )}
            </React.Fragment>
          );
        })}
      </GoogleMap>
    </div>
  );
};

export default LocationMap;
